<template>
  <!-- ✅ Use v-show instead of v-if to prevent layout shifts after localStorage check -->
  <div v-show="showConsentBanner" class="fixed inset-0 bg-gray-900 bg-opacity-50 z-50 flex items-center justify-center">
    <!-- Cookie Consent Modal (Centered) with improved accessibility -->
    <div 
      class="bg-gray-800 text-white rounded-lg p-6 shadow-lg w-11/12 max-w-lg border-t-4 border-purple-500 text-center"
      role="dialog"
      aria-labelledby="cookie-consent-title"
      aria-describedby="cookie-consent-description"
    >
      <h2 id="cookie-consent-title" class="text-xl font-bold mb-4">🍪 We Value Your Privacy</h2>
      <p id="cookie-consent-description" class="text-sm mb-4">
        We use cookies to enhance your experience, analyze site usage, and assist in our marketing efforts.
        By clicking "Accept Cookies," you agree to our use of cookies as described in our
        <a 
          href="/privacy-policy" 
          class="underline text-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-400"
        >
          Privacy Policy
        </a>.
      </p>

      <div class="flex flex-col sm:flex-row justify-center gap-4 mt-4">
        <button
          @click="acceptCookies"
          class="bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 px-6 rounded-lg shadow-md focus:outline-none focus:ring-2 focus:ring-purple-400 focus:ring-offset-2 focus:ring-offset-gray-800"
        >
          Accept Cookies
        </button>
        <button
          @click="openPreferences"
          class="bg-gray-600 hover:bg-gray-500 text-white font-bold py-3 px-6 rounded-lg shadow-md border border-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 focus:ring-offset-gray-800"
        >
          Manage Cookies
        </button>
      </div>
    </div>
  </div>

  <!-- Preferences Modal -->
  <div
    v-show="showPreferences"
    class="fixed inset-0 bg-gray-900 bg-opacity-90 text-white z-50 flex items-center justify-center"
    role="dialog"
    aria-labelledby="preferences-title"
    aria-describedby="preferences-description"
  >
    <div class="bg-gray-800 text-white rounded-lg p-6 shadow-lg w-11/12 max-w-3xl border-t-4 border-purple-500">
      <h2 id="preferences-title" class="text-xl font-bold mb-4">Cookie Preferences</h2>
      <p id="preferences-description" class="text-sm mb-4">
        We only use cookies for analytics purposes to improve your experience. You can enable or disable analytics below.
      </p>
      <div class="flex flex-col gap-4">
        <label class="flex items-center gap-2">
          <input 
            type="checkbox" 
            v-model="preferences.analytics" 
            class="accent-purple-500 w-5 h-5"
            aria-describedby="analytics-description"
          />
          <span>Enable Analytics Cookies</span>
        </label>
        <p id="analytics-description" class="text-xs text-gray-400 ml-7">
          Analytics cookies help us understand how visitors interact with our website. 
          This information is used to improve the user experience.
        </p>
      </div>
      <div class="flex flex-col sm:flex-row justify-center gap-4 mt-6">
        <button
          @click="savePreferences"
          class="bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 px-6 rounded-lg shadow-md focus:outline-none focus:ring-2 focus:ring-purple-400 focus:ring-offset-2 focus:ring-offset-gray-800"
        >
          Save Preferences
        </button>
        <button
          @click="closePreferences"
          class="bg-gray-600 hover:bg-gray-500 text-white font-bold py-3 px-6 rounded-lg shadow-md border border-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 focus:ring-offset-gray-800"
        >
          Cancel
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, nextTick } from 'vue';
import posthog from 'posthog-js';
import { initPostHog, isAnalyticsEnabled } from '@/posthog.js';

// State management with better initialization
const showConsentBanner = ref(false);
const showPreferences = ref(false);
const preferences = ref({
  analytics: true,
});

// Properly initialize on mount with optimized localStorage checks
onMounted(async () => {
  // Render the component first, then check localStorage to prevent layout shifts
  await nextTick();
  
  const storedConsent = localStorage.getItem('cookieConsent');
  const storedPreferences = localStorage.getItem('cookiePreferences');
  
  if (storedConsent === 'true') {
    // User has already consented
    if (storedPreferences) {
      const parsedPreferences = JSON.parse(storedPreferences);
      
      // Initialize tracking if analytics was approved
      if (parsedPreferences.analytics) {
        initPostHog();
      }
    } else {
      // Default to analytics enabled if they accepted cookies but no preferences saved
      initPostHog();
    }
  } else {
    // Show the banner if no consent was given
    showConsentBanner.value = true;
  }
});

// Accept cookies and enable full tracking
const acceptCookies = () => {
  localStorage.setItem('cookieConsent', 'true');
  localStorage.setItem('cookiePreferences', JSON.stringify({ analytics: true }));
  showConsentBanner.value = false;

    // Use the imported function
    initPostHog();
  
  // Announce to screen readers
  announceToScreenReader('Cookies accepted');
};

// Open cookie settings
const openPreferences = () => {
  showPreferences.value = true;
};

// Close preferences modal
const closePreferences = () => {
  showPreferences.value = false;
  
  // Announce to screen readers
  announceToScreenReader('Preferences dialog closed');
};

// Save cookie preferences and send event if analytics is disabled
const savePreferences = () => {
  localStorage.setItem('cookiePreferences', JSON.stringify(preferences.value));
  localStorage.setItem('cookieConsent', 'true');
  
  showConsentBanner.value = false;
  showPreferences.value = false;

  if (preferences.value.analytics) {
    posthog.opt_in_capturing();
  } else {
    posthog.opt_out_capturing();
  }
  
  // Announce to screen readers
  announceToScreenReader('Preferences saved');
};

// Helper function for screen reader announcements
const announceToScreenReader = (message) => {
  const announcement = document.createElement('div');
  announcement.setAttribute('aria-live', 'polite');
  announcement.classList.add('sr-only');
  announcement.textContent = message;
  document.body.appendChild(announcement);
  
  // Clean up after announcement is read
  setTimeout(() => {
    document.body.removeChild(announcement);
  }, 1000);
};
</script>

<style scoped>
/* Ensure modal is centered with a semi-transparent background */
.fixed {
  z-index: 9999; /* Ensure it overlays everything */
}

/* Smooth button transitions */
button {
  transition: background-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
}

h2 {
  font-size: 1.25rem;
  font-weight: 600;
}

p {
  font-size: 0.875rem;
}

/* Screen reader only class */
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
  * {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
    scroll-behavior: auto !important;
  }
}
</style>